import { BasicDataTablePagination, DataTablePagination, IDataTablePaginationProps } from '@common/components/pagination';
import { SearchResponse } from '@common/models';
import { defaultTo } from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import DataTable, { PaginationComponentProps, TableColumn, TableProps } from 'react-data-table-component';
import { dataTableCustomStyles } from './TableStyles';
import { IUnknownPageCountPaginationProps, UnknownPageCountPagination } from '@common/components/pagination/UnknownPageCountPagination';

// Cell Props defines properties needed to create a custom cell defined in your data table columns using TableColumn<T>[]
// i.e. cell: (row, index, col, id) => { <span>{row.name}</span> }
// https://react-data-table-component.netlify.app/?path=/story/columns-cells-custom-cells--custom-cells
export interface ICellProps<T> {
    row: T,
    index?: number,
    column?: TableColumn<T>,
    id?: string | number
}

// To be used when Rows Per Page ddl is implemented in Pager Control
const defaultRowsPerPageOptions = [10, 25, 50, 100];

export type RemoteDataTableProps<T> = {
    searchResult: SearchResponse<T>;
    displayRowsPerPage?: boolean;
    isBasicPagination?: boolean;
    hasMore?: boolean;
	pageRangeDisplayed?: number;
} & Omit<TableProps<T>, "data">

const applyStyleToWhenRowClickable = (rowHasClickEvent: boolean) => {
    if (rowHasClickEvent) { dataTableCustomStyles.rows.style["cursor"] = 'pointer' }
}

export const RemoteDataTable = observer(<T extends unknown>(props: RemoteDataTableProps<T>) => {
    //This is this way so that all possible properties for DataTable can be passed to RemoteDataTable, but we pull off the ones we setup our own defaults for
    //and dump the rest to remainingProps so we can spread the remainingProps onto the data table to support adhocc scenarios like rowClick and other custom case by case scenarios.
    const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        columns, 
        persistTableHead = true, 
        searchResult, 
        displayRowsPerPage, 
        isBasicPagination = false,
        sortServer = true, 
        onSort, 
        defaultSortFieldId, 
        pagination = true, 
        paginationServer = true,
        paginationComponent,
        paginationTotalRows, 
        paginationPerPage = 20,
		pageRangeDisplayed,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        paginationRowsPerPageOptions, onChangePage, onChangeRowsPerPage, onRowDoubleClicked, customStyles, noDataComponent, paginationResetDefaultPage, ...remainingProps
    } = props;

    applyStyleToWhenRowClickable(Boolean(props.onRowClicked) || Boolean(props.onRowDoubleClicked));

    const handleChangePage = (page: number, totalRows: number) => {
        if (onChangePage)
            onChangePage(page - 1, totalRows);
    }

	const setPaginationComponent = (component: PaginationComponentProps) => {
        if (isBasicPagination) {
            let unknownComponent: IUnknownPageCountPaginationProps = {...component, hasMore: props.hasMore };
            return <UnknownPageCountPagination {...unknownComponent} />;
        }
		if (displayRowsPerPage) {
			let dtComponent: IDataTablePaginationProps = {...component, pageRangeDisplayed};
			return <DataTablePagination {...dtComponent} />;
		} else {
			return <BasicDataTablePagination {...component} />;
		}
	}

    return (
        <DataTable
            columns={columns}
            persistTableHead={persistTableHead}
            data={searchResult.results}
            sortServer={sortServer}
            onSort={sortServer && onSort ? onSort : undefined}
            defaultSortFieldId={defaultSortFieldId}
            pagination={pagination}
            paginationServer={paginationServer}
            paginationComponent={setPaginationComponent}
            paginationTotalRows={defaultTo(searchResult?.totalRecordCount, 0)}
            paginationRowsPerPageOptions={defaultRowsPerPageOptions}
            paginationPerPage={paginationPerPage}
            onChangePage={(paginationServer && onChangePage) ? handleChangePage : undefined}
            onChangeRowsPerPage={paginationServer && onChangeRowsPerPage ? onChangeRowsPerPage : undefined}
            customStyles={customStyles == null ? dataTableCustomStyles : customStyles}
            noDataComponent={noDataComponent}
            onRowDoubleClicked={onRowDoubleClicked ? onRowDoubleClicked : undefined}
            paginationResetDefaultPage={paginationResetDefaultPage}
            {...remainingProps}
        />
    )
});
